import {
  InputRule,
  inputRules,
  textblockTypeInputRule,
  wrappingInputRule,
} from "prosemirror-inputrules";
import { Fragment } from "prosemirror-model";
import { TextSelection } from "prosemirror-state";

import { focusSchema } from "../schema";

const headingRule = textblockTypeInputRule(
  /^(#{1,6})\s$/,
  focusSchema.nodes.heading,
  (match) => ({
    level: match[1].length,
  }),
);

const blockquoteRule = wrappingInputRule(
  /^\s*>\s$/,
  focusSchema.nodes.blockquote,
);

const bulletListRule = wrappingInputRule(
  /^\s*([-+*])\s$/,
  focusSchema.nodes.bullet_list,
  () => ({
    tight: true,
  }),
);

const taskListRule = new InputRule(
  /^\[( |x|X)\]\s$/,
  (state, match, start, end) => {
    const $start = state.doc.resolve(start);
    const checked = match[1].toLowerCase() === "x";
    let listItemPos: number | null = null;

    for (let depth = $start.depth; depth > 0; depth--) {
      if ($start.node(depth).type.name === "list_item") {
        listItemPos = $start.before(depth);
        break;
      }
    }

    if (listItemPos == null) {
      return null;
    }

    const listItem = state.doc.nodeAt(listItemPos);

    if (!listItem) {
      return null;
    }

    return state.tr.delete(start, end).setNodeMarkup(listItemPos, undefined, {
      ...listItem.attrs,
      checked,
    });
  },
);

const orderedListRule = wrappingInputRule(
  /^\s*(\d+)\.\s$/,
  focusSchema.nodes.ordered_list,
  (match) => ({
    order: Number(match[1]) || 1,
    tight: true,
  }),
  (match, node) => node.childCount + node.attrs.order === Number(match[1]),
);

const codeBlockRule = textblockTypeInputRule(
  /^```([A-Za-z0-9_-]*)\s$/,
  focusSchema.nodes.code_block,
  (match) => ({
    language: match[1] || "",
  }),
);

const horizontalRule = new InputRule(
  /^(---|\*\*\*|___)$/,
  (state, _match, start) => {
    const { horizontal_rule, paragraph } = focusSchema.nodes;
    const $start = state.doc.resolve(start);

    if (!$start.parent.isTextblock) {
      return null;
    }

    const blockStart = $start.before();
    const blockEnd = $start.after();
    const replacement = Fragment.fromArray([
      horizontal_rule.create(),
      paragraph.create(),
    ]);
    const tr = state.tr.replaceWith(blockStart, blockEnd, replacement);

    tr.setSelection(TextSelection.create(tr.doc, blockStart + 2));

    return tr;
  },
);

export const editorInputRulesPlugin = () =>
  inputRules({
    rules: [
      headingRule,
      blockquoteRule,
      bulletListRule,
      taskListRule,
      orderedListRule,
      codeBlockRule,
      horizontalRule,
    ],
  });
